import React, { useState } from "react";
import Add from "@mui/icons-material/Add";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import { CardLoading } from "../core/CardLoading";
import { enqueueSnackbar } from "notistack";
import { useLinkedGames, useLinkGame, useUnlinkGame, useMyGames } from "@seabrookstudios/pitch2table";
import { FullScreenDialog } from "../core/FullScreenDialog";
import { FilterableGamesList } from "./FilterableGamesList";
import { LinkedGameListItem } from "./ListItem";
import Edit from "@mui/icons-material/Edit";
import CircularProgress from "@mui/material/CircularProgress";
import { CircularProgressSize } from "../theme";
import Refresh from "@mui/icons-material/Refresh";
import Stack from "@mui/material/Stack";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const LinkedGamesCard = ({ gameId }) => {
  const { data: linkedGames = [], isLoading, refetch, isRefetching } = useLinkedGames(gameId);
  const { data: allGames = [] } = useMyGames();
  const linkGame = useLinkGame(gameId);
  const unlinkGame = useUnlinkGame(gameId);
  const [selectedGames, setSelectedGames] = useState(linkedGames.map((linkedGame) => linkedGame.id));

  /**
   * @param {import("@seabrookstudios/types").GameId} linkedGameId
   */
  const handleToggleGame = (linkedGameId) => {
    setSelectedGames((current) => {
      if (current.includes(linkedGameId)) {
        return current.filter((id) => id !== linkedGameId);
      }
      return [...current, linkedGameId];
    });
  };

  const refreshSelectedGames = () => {
    setSelectedGames(linkedGames.map((linkedGame) => linkedGame.id));
  };

  const handleSaveLinkedGames = async () => {
    try {
      const linkedGameIds = linkedGames.map((linkedGame) => linkedGame.id);

      const gamesToLink = selectedGames.filter((id) => !linkedGameIds.includes(id));
      const gamesToUnlink = linkedGameIds.filter((id) => !selectedGames.includes(id));

      console.log({ linkedGameIds, gamesToLink, gamesToUnlink });

      await Promise.all(gamesToLink.map((linkedGameId) => linkGame.mutateAsync({ linkedGameId })));
      await Promise.all(gamesToUnlink.map((linkedGameId) => unlinkGame.mutateAsync({ linkedGameId })));
    } catch {
      enqueueSnackbar("Failed to update linked games", { variant: "error" });
    }
  };

  if (isLoading) {
    return <CardLoading />;
  }

  return (
    <Card elevation={1} square={false}>
      <List>
        <ListItem>
          <ListItemText primary="Related Games" />
          <ListItemSecondaryAction>
            <Stack direction="row" spacing={1}>
              <IconButton size="small" onClick={refetch}>
                {isRefetching ? <CircularProgress size={CircularProgressSize} /> : <Refresh />}
              </IconButton>
              <FullScreenDialog
                title="Related Games"
                canSave={false}
                actionText="SAVE"
                onSaveHandler={handleSaveLinkedGames}
                onOpen={refreshSelectedGames}
                CustomControl={
                  <IconButton color="primary" size="small">
                    {linkedGames.length > 0 ? <Edit /> : <Add />}
                  </IconButton>
                }
              >
                <FilterableGamesList
                  games={allGames.filter((game) => game.id !== gameId)}
                  selectedGames={selectedGames}
                  onToggleGame={handleToggleGame}
                />
              </FullScreenDialog>
            </Stack>
          </ListItemSecondaryAction>
        </ListItem>
        {linkedGames.map((linkedGame) => (
          <LinkedGameListItem key={linkedGame.id} linkedGame={linkedGame} />
        ))}
      </List>
    </Card>
  );
};
